import { ChevronDownIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import { Dropdown } from 'components/form/inputs/Dropdown'
import { useRolodexEdit } from 'features/fde'
import { useKestrel, useKestrelViewerContext } from 'features/kestrelViewer'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { ConfirmationModal } from 'features/kestrelViewer/ui/popupMenu/shared/ConfirmationModal'
import { MarkupLabelInput } from 'features/kestrelViewer/ui/popupMenu/shared/MarkupLabelInput'
import { MarkupNotesInput } from 'features/kestrelViewer/ui/popupMenu/shared/MarkupNotesInput'
import {
  Button,
  ButtonRow,
  FooterSection,
  InputGroup,
  InputHeader,
} from 'features/kestrelViewer/ui/popupMenu/shared/MarkupPopupContainer'
import { useEscapeKey } from 'features/kestrelViewer/ui/popupMenu/shared/useEscapeKey'
import { calculatePrimitiveGeometry } from 'features/kestrelViewer/utils/primitiveUtils'
import {
  formatArea,
  formatVolume,
} from 'features/kestrelViewer/utils/unitConversionUtils'
import { buildTopLabelFromPrimitive } from 'features/kestrelViewer/utils/unitPrimitiveLabelGenerator'
import { useGeckoLocale } from 'features/locale'
import { generateEntrySlug } from 'features/rolodex'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import {
  getPrimitiveGeometryDisplayText,
  KVPrimitive,
  RolodexEntryPatchJson,
  RolodexEntryType,
} from 'types'

import { NDEDataSection } from './ndeData/NdeDataSection'
import { RotationSection } from './RotationSection'

const ReadOnlyField = styled.div`
  min-height: 32px;
  padding: 8px 12px;
  font-size: 0.875rem;
  font-family: inherit;
  line-height: 1.4;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.05);
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
`

const MetricsSection = styled.div`
  margin-top: 8px;
`

const MetricsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.75rem;
  font-weight: 500;
`

const MetricsContent = styled.div<{ $isOpen: boolean }>`
  max-height: ${({ $isOpen }) => ($isOpen ? '500px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
`

const MetricsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 0;
`

const MetricItem = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
`

const MetricLabel = styled.span`
  color: rgba(255, 255, 255, 0.6);
`

const MetricValue = styled.span`
  font-weight: 500;
`

const ScrollableContent = styled.div`
  max-height: 75vh;
  overflow-y: auto;
`

export const PrimitiveMenu: React.FC<{
  selectedPrimitive: KVPrimitive
}> = ({ selectedPrimitive }) => {
  const { setSelectedMarkup, currentUnit } = useKestrelViewerContext()
  const { rolodexPatch } = useRolodexEdit()
  const {
    kestrelModel,
    primitives,
    setPrimitives,
    sendKVCommand,
    unitOptions,
    siteUnits,
    kestrelModelUnits,
    orgId,
    siteId,
    gatekeeper,
    updatePrimitive,
  } = useKestrel()
  const { locale } = useGeckoLocale()
  const labelInputRef = useRef<HTMLInputElement>(null)
  const notesInputRef = useRef<HTMLTextAreaElement>(null)
  const [localLabel, setLocalLabel] = useState(selectedPrimitive.label)
  const [isMetricsOpen, setIsMetricsOpen] = useState(false)

  // Tag Unit state
  const [showTagControls, setShowTagControls] = useState(false)
  const [selectedUnitId, setSelectedUnitId] = useState<string>('')
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false)

  // Handle escape key to close popup
  useEscapeKey(() => setSelectedMarkup(undefined))

  // Focus on label input when popup opens
  useEffect(() => {
    if (labelInputRef.current) {
      labelInputRef.current.focus()
    }
  }, [])

  const handleTabKey = (
    e: React.KeyboardEvent,
    targetRef: React.RefObject<HTMLElement | null>
  ) => {
    if (e.key === 'Tab') {
      e.preventDefault()
      if (targetRef.current) {
        targetRef.current.focus()
      }
    }
  }

  const handleLabelEnter = () => {
    if (!localLabel || localLabel.trim().length === 0) return
    updatePrimitive(
      selectedPrimitive,
      'primitiveLabel',
      (p) => {
        p.label = localLabel
        return p
      },
      localLabel
    )
  }

  const handleLabelEscape = () => {
    setLocalLabel(selectedPrimitive.label)
  }

  const setNotes = async (s: string) => {
    if (!primitives || !kestrelModel?.id) return

    // Update local state
    const updatedPrimitives = primitives.map((p) =>
      p.id === selectedPrimitive.id ? { ...p, notes: s } : p
    )
    setPrimitives(updatedPrimitives)

    // Update rolodex
    const patchData: RolodexEntryPatchJson = {
      id: kestrelModel.id,
      data: {
        primitives: updatedPrimitives.map((p) => KVPrimitive.toJSON(p)),
      },
    }

    try {
      await rolodexPatch([patchData])
    } catch (error) {
      console.error('Failed to update primitive:', error)
    }
  }

  const applyTag = async () => {
    if (!kestrelModel || !selectedUnitId) return

    const existingLink = kestrelModelUnits.find(
      (l) => l.tags[RolodexEntryType.UNIT] === selectedUnitId
    )

    const replaceNeeded = !!existingLink?.data?.primitive

    if (existingLink && replaceNeeded) {
      setShowReplaceConfirm(true)
      return
    }

    await doTag(existingLink?.id)
  }

  const doTag = async (existingLinkId?: string) => {
    if (!kestrelModel || !selectedUnitId) return

    // Fallback org/site from selected unit if missing on model
    const selectedUnit = siteUnits.find((u) => u.id === selectedUnitId)
    const assetName = selectedUnit?.data.name || selectedPrimitive.label
    const primitiveJson = {
      ...KVPrimitive.toJSON(selectedPrimitive),
      primitiveLabel: assetName,
    }
    const annotationJson = buildTopLabelFromPrimitive(primitiveJson, {
      label: assetName,
    })
    const effectiveOrgId = orgId || selectedUnit?.tags.organization
    const effectiveSiteId = siteId || selectedUnit?.tags.site

    try {
      const patch: RolodexEntryPatchJson = existingLinkId
        ? {
            id: existingLinkId,
            data: { primitive: primitiveJson, annotation: annotationJson },
          }
        : {
            id: generateEntrySlug('unit-' + selectedUnitId),
            type: RolodexEntryType.KESTREL_MODEL_UNIT,
            data: { primitive: primitiveJson, annotation: annotationJson },
            tags: {
              ...(effectiveOrgId ? { organization: effectiveOrgId } : {}),
              ...(effectiveSiteId ? { site: effectiveSiteId } : {}),
              [RolodexEntryType.KESTREL_MODEL]: kestrelModel.id,
              [RolodexEntryType.UNIT]: selectedUnitId,
            },
          }
      await rolodexPatch([patch])

      // Remove primitive from model and viewer
      if (primitives && sendKVCommand) {
        const filtered = primitives.filter((p) => p.id !== selectedPrimitive.id)
        setPrimitives(filtered)
        sendKVCommand({
          command: KestrelCommands.DELETE_PRIMITIVE,
          primitiveId: selectedPrimitive.id,
        })
        const patchModel: RolodexEntryPatchJson = {
          id: kestrelModel.id,
          data: { primitives: filtered.map((p) => KVPrimitive.toJSON(p)) },
        }
        await rolodexPatch([patchModel])
      }

      setSelectedMarkup(undefined)
      setShowTagControls(false)
      setSelectedUnitId('')
    } catch (e) {
      console.error('Failed to tag unit:', e)
    }
  }

  const geometryText = getPrimitiveGeometryDisplayText(
    selectedPrimitive.geometry
  )

  // Calculate area and volume from raw primitive dimensions
  const measurementScale = kestrelModel?.data.measurementScale ?? 1
  const { area, volume } = calculatePrimitiveGeometry(
    selectedPrimitive,
    measurementScale
  )

  // Check if area and volume are valid numbers (not undefined, null, NaN, or 0)
  const hasValidArea =
    area !== undefined && area !== null && !isNaN(area) && area > 0

  const hasValidVolume =
    volume !== undefined && volume !== null && !isNaN(volume) && volume > 0

  return (
    <>
      <ScrollableContent>
        <InputGroup>
          <InputHeader>Label</InputHeader>
          <MarkupLabelInput
            ref={labelInputRef}
            value={localLabel}
            onChange={setLocalLabel}
            placeholder='New Primitive'
            onEnter={handleLabelEnter}
            onEscape={handleLabelEscape}
            onTabKey={(e) => handleTabKey(e, notesInputRef)}
            saveOnBlur={true}
            requireNonEmpty={true}
            warningText='Label is required.'
          />
        </InputGroup>

        <InputGroup>
          <InputHeader>Geometry</InputHeader>
          <ReadOnlyField>{geometryText}</ReadOnlyField>
        </InputGroup>

        {hasValidArea && (
          <InputGroup>
            <InputHeader>Area</InputHeader>
            <ReadOnlyField>
              {formatArea(area, currentUnit, locale)}
            </ReadOnlyField>
          </InputGroup>
        )}

        {hasValidVolume && (
          <InputGroup>
            <InputHeader>Volume</InputHeader>
            <ReadOnlyField>
              {formatVolume(volume, currentUnit, locale)}
            </ReadOnlyField>
          </InputGroup>
        )}

        <RotationSection selectedPrimitive={selectedPrimitive} />

        {selectedPrimitive.metrics && (
          <MetricsSection>
            <MetricsHeader onClick={() => setIsMetricsOpen(!isMetricsOpen)}>
              {isMetricsOpen ? (
                <ChevronDownIcon width={16} height={16} />
              ) : (
                <ChevronRightIcon width={16} height={16} />
              )}
              <span>Metrics</span>
            </MetricsHeader>
            <MetricsContent $isOpen={isMetricsOpen}>
              <MetricsList>
                {selectedPrimitive.metrics.points_used !== undefined && (
                  <MetricItem>
                    <MetricLabel>Points Used</MetricLabel>
                    <MetricValue>
                      {selectedPrimitive.metrics.points_used}
                    </MetricValue>
                  </MetricItem>
                )}
                {selectedPrimitive.metrics.fit_quality !== undefined && (
                  <MetricItem>
                    <MetricLabel>Fit Quality</MetricLabel>
                    <MetricValue>
                      {(selectedPrimitive.metrics.fit_quality * 100).toFixed(2)}
                      %
                    </MetricValue>
                  </MetricItem>
                )}
                {selectedPrimitive.metrics.rms_distance !== undefined && (
                  <MetricItem>
                    <MetricLabel>RMS Distance</MetricLabel>
                    <MetricValue>
                      {selectedPrimitive.metrics.rms_distance.toFixed(3)}
                    </MetricValue>
                  </MetricItem>
                )}
                {selectedPrimitive.metrics.max_distance !== undefined && (
                  <MetricItem>
                    <MetricLabel>Max Distance</MetricLabel>
                    <MetricValue>
                      {selectedPrimitive.metrics.max_distance.toFixed(3)}
                    </MetricValue>
                  </MetricItem>
                )}
                {selectedPrimitive.metrics.mean_distance !== undefined && (
                  <MetricItem>
                    <MetricLabel>Mean Distance</MetricLabel>
                    <MetricValue>
                      {selectedPrimitive.metrics.mean_distance.toFixed(3)}
                    </MetricValue>
                  </MetricItem>
                )}
              </MetricsList>
            </MetricsContent>
          </MetricsSection>
        )}

        <InputGroup>
          <InputHeader>Notes</InputHeader>
          <MarkupNotesInput
            ref={notesInputRef}
            value={selectedPrimitive.notes}
            onChange={setNotes}
            placeholder='Add notes...'
            onTabKey={(e) => handleTabKey(e, labelInputRef)}
          />
        </InputGroup>

        <NDEDataSection selectedPrimitive={selectedPrimitive} />

        <FooterSection>
          {gatekeeper.feature.unitTaggingControls && showTagControls && (
            <InputGroup>
              <InputHeader>Select Unit</InputHeader>
              <div onMouseDown={(e) => e.stopPropagation()}>
                <Dropdown
                  options={unitOptions}
                  onValueChange={(v) => setSelectedUnitId(v)}
                  placeholder='Choose a unit...'
                />
              </div>
            </InputGroup>
          )}
          {gatekeeper.feature.unitTaggingControls && (
            <ButtonRow>
              <Button onClick={() => setShowTagControls((s) => !s)}>
                {showTagControls ? 'Hide' : 'Tag Unit'}
              </Button>
              {showTagControls && (
                <Button disabled={!selectedUnitId} onClick={() => applyTag()}>
                  Apply
                </Button>
              )}
            </ButtonRow>
          )}
        </FooterSection>

        {gatekeeper.feature.unitTaggingControls && (
          <ConfirmationModal
            isOpen={showReplaceConfirm}
            title='Replace existing unit tag?'
            message={
              'This unit already has a tagged primitive.\nDo you want to replace it?'
            }
            confirmText='Replace'
            cancelText='Cancel'
            onConfirm={() => {
              setShowReplaceConfirm(false)
              doTag(
                kestrelModelUnits.find(
                  (l) => l.tags[RolodexEntryType.UNIT] === selectedUnitId
                )?.id
              )
            }}
            onCancel={() => setShowReplaceConfirm(false)}
          />
        )}
      </ScrollableContent>
    </>
  )
}
